import { type CreateLocationInput, locationIdSchema, type UpdateLocationInput } from "@repo/api/schemas";
import { type LocationDto, useLocations } from "@repo/data-store";
import { mintEntityId } from "@repo/domain";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import { useAppForm, withForm } from "@/shared/forms/app-form";
import { getLocationDescendantIds } from "@/shared/lib/resource-utils";
import { slugify } from "@/shared/lib/slugify";

// === Schema & Types ===

/**
 * A location's OWN fields. Name, description and status are not here: the
 * definition shell owns those three on every kind, so a location form that
 * also declared them would be a second source of truth for the same values.
 */
export const locationDetailsSchema = z.object({
  type: z.string(),
  physicalType: z.string().min(1, "Physical type is required"),
  parentId: z.string(),
});

export type LocationDetailsValues = z.infer<typeof locationDetailsSchema>;

export const DEFAULT_LOCATION_DETAILS: LocationDetailsValues = {
  type: "",
  physicalType: "",
  parentId: "none",
};

export const LOCATION_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "inactive", label: "Inactive" },
];

export const PHYSICAL_TYPE_OPTIONS = [
  { value: "Site", label: "Site" },
  { value: "Group", label: "Group" },
  { value: "Room", label: "Room" },
];

/** Physical types that map to mode=instance (concrete schedulable locations) */
export const INSTANCE_PHYSICAL_TYPES = new Set(["Room"]);

/** Physical types that don't require a parent location */
export const TOP_LEVEL_PHYSICAL_TYPES = new Set(["Site"]);

/** Physical types that act as category groups (mode=kind, requires type + parent) */
export const GROUP_PHYSICAL_TYPES = new Set(["Group"]);

// === Converters ===

export function locationToDetails(location: LocationDto): LocationDetailsValues {
  return {
    type: location.type?.[0]?.display ?? location.type?.[0]?.text ?? "",
    physicalType: location.physicalType?.text ?? "",
    parentId: location.partOfLocationId ?? "none",
  };
}

/** The shell's three fields plus the location's own, assembled into one intent input. */
function buildLocationFields(
  shell: { title: string; description: string; status: string },
  details: LocationDetailsValues,
) {
  const parentId = details.parentId === "none" ? undefined : details.parentId;
  // No `mode`: FHIR's Location.mode is absent from the create schema, the
  // update schema, the handler and the domain model, so anything passed here
  // was dropped on the floor. Persisting it is a write-path change, not a
  // form change.
  return {
    name: shell.title,
    status: shell.status as "active" | "suspended" | "inactive",
    description: shell.description || undefined,
    type: details.type ? [{ coding: [{ code: slugify(details.type), display: details.type }] }] : undefined,
    physicalType: details.physicalType ? { text: details.physicalType } : undefined,
    partOfId: parentId ? locationIdSchema.parse(parentId) : undefined,
  };
}

export function buildCreateLocationInput(
  shell: { title: string; description: string; status: string },
  details: LocationDetailsValues,
): CreateLocationInput {
  return { ...buildLocationFields(shell, details), id: mintEntityId(), scheduleId: mintEntityId() };
}

export function buildUpdateLocationInput(
  id: UpdateLocationInput["id"],
  shell: { title: string; description: string; status: string },
  details: LocationDetailsValues,
): UpdateLocationInput {
  const fields = buildLocationFields(shell, details);
  return {
    ...fields,
    id,
    description: fields.description ?? null,
    type: GROUP_PHYSICAL_TYPES.has(details.physicalType) ? (fields.type ?? []) : [],
    partOfId: fields.partOfId ?? null,
  };
}

// === Hook ===

interface UseLocationFormOptions {
  initialValues?: LocationDto;
}

interface UseLocationFormReturn {
  defaultValues: LocationDetailsValues;
  validators: { onDynamic: typeof locationDetailsSchema };
  allLocations: LocationDto[];
  /** `label` is the location's display path — a plain string the picker can search. */
  parentOptions: Array<{ value: string; label: string }>;
  siteParentOptions: Array<{ value: string; label: string }>;
  instanceParentOptions: Array<{ value: string; label: string }>;
}

export function useLocationFormData({ initialValues }: UseLocationFormOptions): UseLocationFormReturn {
  const { locations: allLocations } = useLocations();

  const excludedIds = useMemo(() => {
    if (!initialValues?.locationId) return new Set<string>();
    const descendants = getLocationDescendantIds(initialValues.locationId, allLocations);
    descendants.add(initialValues.locationId);
    return descendants;
  }, [allLocations, initialValues?.locationId]);

  /** Sites only — for Group parent picker */
  const siteParents = useMemo(
    () => allLocations.filter((l) => l.physicalType?.text === "Site" && !excludedIds.has(l.locationId ?? "")),
    [allLocations, excludedIds],
  );

  /** Sites + Groups — for Room parent picker (rooms can be children of either) */
  const instanceParents = useMemo(
    () =>
      allLocations.filter(
        (l) =>
          (l.physicalType?.text === "Site" || l.physicalType?.text === "Group") && !excludedIds.has(l.locationId ?? ""),
      ),
    [allLocations, excludedIds],
  );

  // `displayPath` comes from the location view, so labels are plain strings the
  // picker can search; the caller decides whether to render the ancestry pill.
  const buildParentOptions = useCallback(
    (parents: LocationDto[]) => [
      { value: "none", label: "None" },
      ...parents.map((loc) => ({ value: loc.locationId, label: loc.displayPath })),
    ],
    [],
  );

  const siteParentOptions = useMemo(() => buildParentOptions(siteParents), [buildParentOptions, siteParents]);
  const instanceParentOptions = useMemo(
    () => buildParentOptions(instanceParents),
    [buildParentOptions, instanceParents],
  );

  /** Default parent options based on initial physical type */
  const parentOptions: Array<{ value: string; label: string }> = useMemo(() => {
    const pt = initialValues?.physicalType?.text;
    if (pt === "Room") return instanceParentOptions;
    if (pt === "Group") return siteParentOptions;
    return [{ value: "none", label: "None" }];
  }, [initialValues?.physicalType?.text, instanceParentOptions, siteParentOptions]);

  const defaultValues = initialValues ? locationToDetails(initialValues) : DEFAULT_LOCATION_DETAILS;

  return {
    defaultValues,
    validators: { onDynamic: locationDetailsSchema },
    allLocations,
    parentOptions,
    siteParentOptions,
    instanceParentOptions,
  };
}

export { useAppForm, withForm };
